'use client';
import React, { useState } from "react";
import QRCode from "../QRCode/QRCode";
import DropdownList from "../DropdownList/DropdownList";
import { useDetectWrap } from '@/hooks/useDetectWrap';
import { FacebookReviewSectionContent, LanguageType } from '@/lib/types';
import { GoogleReviewSectionContent } from '@/lib/types';
import { useIsLg } from '@/contexts/BreakpointContext';

type QRCardType = "default" | "dropdown";

interface QRCardProps {
	type: QRCardType
	googleReviewSectionPageData: GoogleReviewSectionContent;
	facebookReviewSectionPageData: FacebookReviewSectionContent;
	icon: "facebook" | "google";
	/** Forwarded to the inner QRCode — when set, the QR image itself becomes
	 *  the clickable link that opens this URL in a new tab. */
	href?: string;
	/** Drives RTL behavior: in Arabic the icon/dropdown row reverses so the
	 *  dropdown sits to the right of the icon (visual mirror of English), and
	 *  the DropdownList renders in its Arabic variant. */
	language?: LanguageType;
}

const QRCard: React.FC<QRCardProps> = ({
	type,
	googleReviewSectionPageData,
	facebookReviewSectionPageData,
	icon,
	href,
	language = "english"
}) => {
	const isLg = useIsLg();
	const { ref, isWrapped } = useDetectWrap();

	// Get unique branches
	const branches = facebookReviewSectionPageData.facebookReviews.map(review => review.branch?.branchName || '').filter(Boolean);
	const uniqueBranches = [...new Set(branches)];
	const hasSingleBranch = uniqueBranches.length === 1;
	const defaultBranch = hasSingleBranch ? uniqueBranches[0] : null;

	const [selectedBranch, setSelectedBranch] = useState<string | null>(
		type === "dropdown" ? (defaultBranch || facebookReviewSectionPageData.facebookReviews[0]?.branch?.branchName || null) : null
	);

	const handleBranchChange = (value: string) => {
		setSelectedBranch(value);
	};

	return (
		<div className='
			w-full tb:w-[21.9375rem] tb:flex-grow
			flex flex-col
			gap-y-[2.5rem]
			lg:gap-y-[7rem]

			bg-bg-alpha-light/16

			px-[1.5rem]
			tb:px-[2.5rem]
			lg:px-[3.5rem]

			py-[2.5rem]
			tb:py-[2.5rem]
			lg:py-[3.5rem]

			border-radius-main

			outline-main
			outline-stroke-soft-200
			'>
			<div className={`
					w-full
					flex flex-col ${language === 'arabic' ? 'tb:flex-row-reverse' : 'tb:flex-row'} tb:flex-grow tb:flex-wrap tb:justify-between items-center
					gap-y-[1rem]
					gap-x-[1rem]
					${type === "dropdown" ? '' : ''}
				`}>
				<div className='flex items-center w-fit flex-grow max-w-full'
					style={{
						justifyContent: type === "dropdown" && !isWrapped && !hasSingleBranch ? 'flex-start' : 'center'
					}}
				>
					<div className="
						rounded-full
						px-[0.75rem] py-[0.75rem] lg:px-[1.1875rem] lg:py-[1.1875rem]
						outline-main outline-stroke-soft-200
						bg-bg-light-primary
						w-fit">
						<img src={icon === "facebook" ? "/Brand-Facebook-No-Padding.svg" : "/Brand-Google.svg"} alt={icon === "facebook" ? "Facebook icon" : "Google icon"} className="w-[2rem] h-[2rem] lg:w-[2.875rem] lg:h-[2.875rem]" />
					</div>
				</div>
				{type === "dropdown" && !hasSingleBranch &&
					<div
						ref={ref}
						className="w-full flex items-center justify-center tb:flex-grow tb:w-[18rem] tb:max-w-[24.3125rem]">
						<DropdownList
							size={isLg ? 20 : 18}
							language={language}
							value={selectedBranch}
							onChange={handleBranchChange}
							options={facebookReviewSectionPageData.facebookReviews.map(review => ({
								label: review.branch?.branchName || '',
								value: review.branch?.branchName || ''
							}))}
						/>
					</div>
				}
			</div>

			<div className="w-full flex items-center justify-center">
				<QRCode
					type={type}
					googleReviewSectionPageData={googleReviewSectionPageData}
					facebookReviewSectionPageData={facebookReviewSectionPageData}
					selectedBranch={selectedBranch}
					href={href}
				/>
			</div>

			<p className='
				w-full
				text-center
				text-text-primary
				text-36-to-20--neue-june
				'>
				{
					type === "default" ?
					googleReviewSectionPageData.googleReviewTitle
					 :
					facebookReviewSectionPageData.facebookReviews.find(review => review.branch?.branchName === selectedBranch)?.facebookReviewTitle ||
					''
				}
			</p>

		</div>
	);
};

export default QRCard;
